import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bot,
  CircleCheck,
  CircleX,
  Clock,
  Hand,
  MessageSquareWarning,
  Undo2,
  UserCheck,
  UserCog,
} from 'lucide-react'
import {
  COMPLEMENT_DEFAULT_DAYS,
  DECISION_REASON_MIN,
  type CheckStatus,
  type ComplementInfo,
  type DecisionBlocker,
  type DecisionInfo,
  type SystemCheck,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Modal,
  Textarea,
  useAuth,
  useToast,
  type BadgeVariant,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import { APPLICATIONS_KEY } from './ApplicationsPage'

/** Données de recevabilité de la fiche (phase 12). */
export interface ReviewState {
  id: string
  status: string
  competition: { id: string }
  assignee: { id: string; name: string } | null
  reviewStartedAt: string | null
  decidedAt: string | null
  systemChecks: SystemCheck[]
  blockers: DecisionBlocker[]
  decisions: DecisionInfo[]
  complements: ComplementInfo[]
  can: {
    takeCharge: boolean
    assign: boolean
    assignOthers: boolean
    requestComplement: boolean
    propose: boolean
    validate: boolean
  }
}

const CHECK_VARIANT: Record<CheckStatus, BadgeVariant> = {
  ok: 'success',
  warning: 'warning',
  failed: 'danger',
  pending: 'info',
}
const CHECK_LABEL: Record<CheckStatus, string> = {
  ok: 'Conforme',
  warning: 'À examiner',
  failed: 'Non conforme',
  pending: 'En attente',
}

/** Envoi d'une action d'instruction puis rafraîchissement de la fiche et des listes. */
function useReviewAction<T>(appId: string, path: string, success: string) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: T) => session.api.post(`/admin/applications/${appId}/${path}`, body),
    onSuccess: async () => {
      toast.success(success)
      await queryClient.invalidateQueries({ queryKey: APPLICATIONS_KEY })
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
}

/**
 * Instruction du dossier : agent chargé, actions disponibles selon les droits
 * et l'état, proposition en attente de validation (quatre yeux).
 */
export function InstructionCard({ d }: { d: ReviewState }) {
  const { user } = useAuth()
  const [modal, setModal] = useState<null | 'complement' | 'not_admissible' | 'return' | 'assign'>(
    null
  )
  const takeCharge = useReviewAction<object>(d.id, 'take-charge', 'Dossier pris en charge')
  const assignSelf = useReviewAction<{ assignee: string | null }>(
    d.id,
    'assign',
    'Attribution enregistrée'
  )
  const propose = useReviewAction<{ decision: string; reason?: string | null }>(
    d.id,
    'decisions',
    'Proposition transmise pour validation'
  )
  const validate = useReviewAction<{ action: 'approve' | 'return'; note?: string | null }>(
    d.id,
    'decisions/validate',
    'Décision enregistrée'
  )
  const pending = d.decisions.find((x) => x.status === 'proposed') ?? null
  const open = d.complements.find((c) => c.status === 'open') ?? null
  const mine = d.assignee?.id === user?.id
  const anyAction =
    d.can.takeCharge || d.can.assign || d.can.requestComplement || d.can.propose || d.can.validate

  return (
    <Card>
      <CardHeader
        title="Instruction"
        actions={
          d.assignee ? (
            <Badge variant="neutral">
              <UserCheck aria-hidden="true" size={14} /> {mine ? 'Moi' : d.assignee.name}
            </Badge>
          ) : (
            <Badge variant="outline">Non attribué</Badge>
          )
        }
      />
      <CardBody>
        <div className="stack" style={{ gap: 'var(--space-4)' }}>
          {pending && (
            <Alert
              tone={pending.decision === 'admissible' ? 'info' : 'warning'}
              title={`Proposition en attente de validation : ${pending.decisionLabel.toLowerCase()}`}
            >
              <p style={{ margin: 0 }}>
                {pending.bySystem ? (
                  <>
                    <Badge variant="sahel">
                      <Bot aria-hidden="true" size={12} /> Système
                    </Badge>{' '}
                    Contrôle automatique, {when(pending.proposedAt)}.
                  </>
                ) : (
                  <>
                    Proposée par <strong>{pending.proposedBy?.name}</strong>,{' '}
                    {when(pending.proposedAt)}.
                  </>
                )}
              </p>
              {pending.reason && (
                <p style={{ margin: 'var(--space-2) 0 0' }}>Motif : {pending.reason}</p>
              )}
              {d.can.validate ? (
                <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                  <Button
                    variant="primary"
                    size="sm"
                    icon={<CircleCheck />}
                    loading={validate.isPending && validate.variables?.action === 'approve'}
                    onClick={() => validate.mutate({ action: 'approve' })}
                  >
                    Valider la décision
                  </Button>
                  <Button size="sm" icon={<Undo2 />} onClick={() => setModal('return')}>
                    Renvoyer pour réexamen
                  </Button>
                </div>
              ) : pending.proposedBy?.id === user?.id ? (
                <p className="text-muted" style={{ margin: 'var(--space-2) 0 0' }}>
                  Votre proposition doit être validée par une autre personne habilitée.
                </p>
              ) : null}
            </Alert>
          )}

          {open && (
            <Alert tone="warning" title={`Complément attendu avant le ${when(open.dueAt)}`}>
              {open.reason}
            </Alert>
          )}

          {d.can.propose && d.blockers.length > 0 && (
            <div>
              <p className="field-label" style={{ marginBottom: 'var(--space-2)' }}>
                Avant de proposer « recevable »
              </p>
              <ul className="review-blockers">
                {d.blockers.map((b, i) => (
                  <li key={`${b.code}-${i}`}>{b.message}</li>
                ))}
              </ul>
            </div>
          )}

          {anyAction ? (
            <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {d.can.takeCharge && (
                <Button
                  variant="primary"
                  icon={<Hand />}
                  loading={takeCharge.isPending}
                  onClick={() => takeCharge.mutate({})}
                >
                  Prendre en charge
                </Button>
              )}
              {d.can.assign && !mine && (
                <Button
                  icon={<UserCheck />}
                  loading={assignSelf.isPending}
                  onClick={() => user && assignSelf.mutate({ assignee: user.id })}
                >
                  M’attribuer le dossier
                </Button>
              )}
              {d.can.assignOthers && (
                <Button icon={<UserCog />} onClick={() => setModal('assign')}>
                  Attribuer à…
                </Button>
              )}
              {d.can.requestComplement && (
                <Button icon={<MessageSquareWarning />} onClick={() => setModal('complement')}>
                  Demander un complément
                </Button>
              )}
              {d.can.propose && (
                <>
                  <Button
                    variant="primary"
                    icon={<CircleCheck />}
                    disabled={d.blockers.length > 0}
                    loading={propose.isPending && propose.variables?.decision === 'admissible'}
                    onClick={() => propose.mutate({ decision: 'admissible' })}
                  >
                    Proposer : recevable
                  </Button>
                  <Button
                    variant="danger"
                    icon={<CircleX />}
                    onClick={() => setModal('not_admissible')}
                  >
                    Proposer : non recevable
                  </Button>
                </>
              )}
            </div>
          ) : (
            !pending && (
              <p className="text-muted" style={{ margin: 0 }}>
                {d.decidedAt
                  ? `Décision validée le ${when(d.decidedAt)}.`
                  : 'Aucune action disponible pour vous sur ce dossier.'}
              </p>
            )
          )}
        </div>
      </CardBody>

      {modal === 'complement' && <ComplementModal appId={d.id} onClose={() => setModal(null)} />}
      {modal === 'assign' && (
        <AssignModal
          appId={d.id}
          competitionId={d.competition.id}
          current={d.assignee?.id ?? null}
          onClose={() => setModal(null)}
        />
      )}
      {(modal === 'not_admissible' || modal === 'return') && (
        <ReasonModal
          title={modal === 'return' ? 'Renvoyer la proposition' : 'Proposer : non recevable'}
          description={
            modal === 'return'
              ? 'La proposition est renvoyée à l’instruction. Le motif est conservé dans l’historique des décisions.'
              : 'La décision sera effective après validation par une autre personne habilitée. Le motif est communiqué au candidat.'
          }
          label={modal === 'return' ? 'Motif du renvoi' : 'Motif de non-recevabilité'}
          confirm={modal === 'return' ? 'Renvoyer' : 'Transmettre pour validation'}
          tone={modal === 'return' ? 'secondary' : 'danger'}
          pending={modal === 'return' ? validate.isPending : propose.isPending}
          error={
            modal === 'return'
              ? fieldErrors(validate.error).note
              : fieldErrors(propose.error).reason
          }
          onClose={() => setModal(null)}
          onConfirm={(text) =>
            (modal === 'return'
              ? validate.mutateAsync({ action: 'return', note: text })
              : propose.mutateAsync({ decision: 'not_admissible', reason: text })
            ).then(() => setModal(null))
          }
        />
      )}
    </Card>
  )
}

function ReasonModal({
  title,
  description,
  label,
  confirm,
  tone,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  title: string
  description: string
  label: string
  confirm: string
  tone: 'danger' | 'secondary'
  pending: boolean
  error?: string
  onClose: () => void
  onConfirm: (text: string) => Promise<unknown>
}) {
  const [text, setText] = useState('')
  const short = text.trim().length < DECISION_REASON_MIN
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant={tone}
            loading={pending}
            disabled={short}
            onClick={() => void onConfirm(text.trim()).catch(() => undefined)}
          >
            {confirm}
          </Button>
        </>
      }
    >
      <Textarea
        label={label}
        required
        rows={3}
        value={text}
        error={error}
        hint={`${DECISION_REASON_MIN} caractères minimum.`}
        onChange={(e) => setText(e.target.value)}
      />
    </Modal>
  )
}

/** Valeur par défaut d'un champ `datetime-local` : maintenant + n jours, 23 h 59. */
function defaultDue(days: number) {
  const date = new Date(Date.now() + days * 86_400_000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T23:59`
}

function ComplementModal({ appId, onClose }: { appId: string; onClose: () => void }) {
  const [reason, setReason] = useState('')
  const [due, setDue] = useState(defaultDue(COMPLEMENT_DEFAULT_DAYS))
  const request = useReviewAction<{ reason: string; dueAt: string }>(
    appId,
    'complements',
    'Complément demandé au candidat'
  )
  const errors = fieldErrors(request.error)
  return (
    <Modal
      open
      onClose={onClose}
      title="Demander un complément"
      description="Le candidat est prévenu par courriel. Les pièces refusées et la copie de la pièce d’identité refusée lui sont listées ; il ne peut remplacer que celles-ci. Sans réponse à la date limite, le dossier revient en instruction."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={request.isPending}
            disabled={reason.trim().length < DECISION_REASON_MIN || !due}
            onClick={() =>
              request
                .mutateAsync({ reason: reason.trim(), dueAt: new Date(due).toISOString() })
                .then(onClose)
                .catch(() => undefined)
            }
          >
            Envoyer la demande
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        <Textarea
          label="Ce qui est demandé au candidat"
          required
          rows={4}
          value={reason}
          error={errors.reason}
          hint={`${DECISION_REASON_MIN} caractères minimum. Ce texte est envoyé au candidat.`}
          onChange={(e) => setReason(e.target.value)}
        />
        <Input
          label="Date limite (heure locale)"
          type="datetime-local"
          required
          value={due}
          error={errors.dueAt}
          onChange={(e) => setDue(e.target.value)}
        />
      </div>
    </Modal>
  )
}

function AssignModal({
  appId,
  competitionId,
  current,
  onClose,
}: {
  appId: string
  competitionId: string
  current: string | null
  onClose: () => void
}) {
  const { session } = useAuth()
  const [assignee, setAssignee] = useState(current ?? '')
  const reviewers = useQuery({
    queryKey: ['admin', 'reviewers', competitionId],
    queryFn: () =>
      session.api
        .get<{ data: { id: string; name: string; email: string }[] }>(
          '/admin/applications/reviewers',
          { query: { competition: competitionId } }
        )
        .then((r) => r.data),
  })
  const assign = useReviewAction<{ assignee: string | null }>(
    appId,
    'assign',
    'Attribution enregistrée'
  )
  return (
    <Modal
      open
      onClose={onClose}
      title="Attribuer le dossier"
      description="Seuls les agents habilités à instruire les dossiers de ce concours sont proposés."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={assign.isPending}
            onClick={() =>
              assign
                .mutateAsync({ assignee: assignee || null })
                .then(onClose)
                .catch(() => undefined)
            }
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <label className="field">
        <span className="field-label">Agent chargé du dossier</span>
        <select className="select" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <option value="">— Non attribué —</option>
          {(reviewers.data ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} ({r.email})
            </option>
          ))}
        </select>
      </label>
    </Modal>
  )
}

/** 🤖 Contrôles automatiques : badges « Système », distincts des décisions humaines. */
export function SystemChecksCard({ checks }: { checks: SystemCheck[] }) {
  if (!checks.length) return null
  return (
    <Card>
      <CardHeader
        title="Contrôles automatiques"
        actions={
          <Badge variant="sahel">
            <Bot aria-hidden="true" size={12} /> Système
          </Badge>
        }
      />
      <CardBody>
        <ul className="check-list">
          {checks.map((c) => (
            <li key={c.code} data-status={c.status}>
              <div>
                <strong>{c.label}</strong>
                <p>{c.detail}</p>
              </div>
              <Badge variant={CHECK_VARIANT[c.status]} dot>
                {CHECK_LABEL[c.status]}
              </Badge>
            </li>
          ))}
        </ul>
        <p
          className="text-muted"
          style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--text-sm)' }}
        >
          Ces contrôles éclairent l’instruction ; seule une décision humaine validée change la
          recevabilité du dossier.
        </p>
      </CardBody>
    </Card>
  )
}

const DECISION_VARIANT = { proposed: 'warning', validated: 'success', returned: 'neutral' } as const

/** Décisions de recevabilité : auteur, date, validation (cahier §10.2). */
export function DecisionsCard({ decisions }: { decisions: DecisionInfo[] }) {
  return (
    <Card>
      <CardHeader title="Décisions de recevabilité" />
      <CardBody>
        {decisions.length ? (
          <ol className="timeline">
            {decisions.map((x) => (
              <li key={x.id}>
                <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                  <strong>{x.decisionLabel}</strong>
                  <Badge variant={DECISION_VARIANT[x.status]}>{x.statusLabel}</Badge>
                  {x.bySystem && (
                    <Badge variant="sahel">
                      <Bot aria-hidden="true" size={12} /> Système
                    </Badge>
                  )}
                </div>
                {x.reason && <p style={{ margin: 'var(--space-1) 0' }}>Motif : {x.reason}</p>}
                <div className="timeline-meta">
                  Proposée {x.bySystem ? 'par le système' : `par ${x.proposedBy?.name ?? '—'}`},{' '}
                  {when(x.proposedAt)}
                  {x.validatedBy &&
                    ` · ${x.status === 'returned' ? 'renvoyée' : 'validée'} par ${x.validatedBy.name}, ${when(x.validatedAt)}`}
                </div>
                {x.validationNote && <div className="timeline-meta">Note : {x.validationNote}</div>}
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-muted" style={{ margin: 0 }}>
            Aucune décision pour l’instant. Une décision est proposée par un agent puis validée par
            une autre personne habilitée.
          </p>
        )}
      </CardBody>
    </Card>
  )
}

export function ComplementsCard({ complements }: { complements: ComplementInfo[] }) {
  if (!complements.length) return null
  return (
    <Card>
      <CardHeader title="Demandes de complément" />
      <CardBody>
        <ol className="timeline">
          {complements.map((c) => (
            <li key={c.id}>
              <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <strong>Complément</strong>
                <Badge
                  variant={
                    c.status === 'open'
                      ? 'warning'
                      : c.status === 'answered'
                        ? 'success'
                        : 'neutral'
                  }
                >
                  {c.statusLabel}
                </Badge>
              </div>
              <p style={{ margin: 'var(--space-1) 0' }}>{c.reason}</p>
              {c.items.length > 0 && (
                <ul style={{ margin: '0 0 var(--space-1)', paddingInlineStart: '1.2em' }}>
                  {c.items.map((i) => (
                    <li key={`${i.kind}-${i.ref}`}>
                      {i.label}
                      {i.reason ? ` — ${i.reason}` : ''}
                    </li>
                  ))}
                </ul>
              )}
              <div className="timeline-meta">
                Demandé par {c.requestedBy?.name ?? '—'}, {when(c.requestedAt)} · échéance{' '}
                {when(c.dueAt)}
                {c.answeredAt && ` · réponse le ${when(c.answeredAt)}`}
              </div>
              {c.answerMessage && (
                <div className="timeline-meta">
                  <Clock aria-hidden="true" size={12} /> Message du candidat : {c.answerMessage}
                </div>
              )}
            </li>
          ))}
        </ol>
      </CardBody>
    </Card>
  )
}
